import "./App.css";
import PageRouter from "./router";
import React, { useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useAppRegistryStore } from "./stores/appRegistryStore";
import { getThemeById } from "./data/themeColors";
import "./i18n";
import i18n from "./i18n";

function App() {
  const settings = useAppRegistryStore((s) => s.settings);
  const isDark = settings?.currentTheme === "dark";
  const selectedId = settings?.selectedTheme || "default";
  const themeColors = getThemeById(selectedId);

  React.useEffect(() => {
    const root = document.documentElement;
    if (document.body) {
      document.body.classList.toggle("dark", Boolean(isDark));
    }
    if (themeColors && themeColors.colors) {
      Object.entries(themeColors.colors).forEach(([key, value]) => {
        const cssVar = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        root.style.setProperty(`--${cssVar}`, value);
      });
      if (isDark) {
        root.style.setProperty("--bg-primary", "#121212");
        root.style.setProperty("--text-primary", "#e0e0e0");
        root.style.setProperty("--card-bg", "#1e1e1e");
        root.style.setProperty("--background", "#121212");
        root.style.setProperty("--foreground", "#e0e0e0");
        root.style.setProperty("--card", "#1e1e1e");
        root.style.setProperty("--paper", "#1e1e1e");
        root.style.setProperty("--navbar", "#1e1e1e");
        root.style.setProperty("--toolbar", "#1e1e1e");
      } else {
        root.style.setProperty(
          "--bg-primary",
          themeColors.colors.background || "#ffffff",
        );
        root.style.setProperty(
          "--text-primary",
          themeColors.colors.foreground || "#1a1a1a",
        );
        root.style.setProperty(
          "--card-bg",
          themeColors.colors.card || "#ffffff",
        );
      }
    }
  }, [selectedId, isDark]);

  const muiTheme = useMemo(() => {
    return createTheme({
      palette: {
        mode: isDark ? "dark" : "light",
        primary: {
          main: isDark ? "#90caf9" : themeColors?.colors?.primary || "#1976d2",
        },
        secondary: {
          main: isDark
            ? "#f48fb1"
            : themeColors?.colors?.secondary || "#9c27b0",
        },
        background: {
          default: isDark
            ? "#121212"
            : themeColors?.colors?.background || "#ffffff",
          paper: isDark ? "#1e1e1e" : themeColors?.colors?.paper || "#ffffff",
        },
        text: {
          primary: isDark
            ? "#e0e0e0"
            : themeColors?.colors?.foreground || "#1a1a1a",
          secondary: isDark ? "#b0b0b0" : "#6c757d",
        },
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: "var(--navbar) !important",
              color: "var(--text-primary) !important",
            },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              backgroundColor: "var(--toolbar) !important",
              color: "var(--text-primary) !important",
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundColor: "var(--background) !important",
              color: "var(--text-primary) !important",
            },
          },
        },
      },
    });
  }, [isDark, selectedId]);

  React.useEffect(() => {
    if (settings?.selectedLanguage) {
      i18n.changeLanguage(settings.selectedLanguage);
    }
  }, [settings?.selectedLanguage]);

  React.useEffect(() => {
    const fontSize = settings?.fontSize || 100;
    document.documentElement.style.fontSize = `${fontSize}%`;
    document.documentElement.style.setProperty(
      "--font-scale",
      String(fontSize / 100),
    );
  }, [settings?.fontSize]);

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="App">
        <PageRouter />
      </div>
    </ThemeProvider>
  );
}

export default App;
